// ABOUTME: Disable-confirmation warning listing every destination flow a polling disable stops, peers included
import { usePollingConfigs } from '../../hooks/useAdminQueries';
import { INTEGRATION_COLORS } from './integrationColors';
import { byDestination, partitionConfigsByOperator } from './operatorFlows';

// A destination flow rendered as a coloured chip. `operatorLabel` names the
// operator the flow serves; `muted` greys it for other-operator flows.
function DestChip({
  destination,
  operatorLabel,
  muted,
}: {
  destination: string;
  operatorLabel?: string;
  muted?: boolean;
}) {
  return (
    <span
      className={`dest-chip${muted ? ' muted' : ''}`}
      style={{ '--chip-color': INTEGRATION_COLORS[destination] } as React.CSSProperties}
    >
      <span data-testid={muted ? 'warning-chip-other' : 'warning-chip'}>{destination}</span>
      {operatorLabel && <span className="dest-chip-op">{operatorLabel}</span>}
    </span>
  );
}

// Lists the destination flows a disable stops, as coloured chips. The viewed
// operator's flows come first; when the source is shared, a separated, greyed
// block names the other operators the same disable also halts — so the blast
// radius of a shared origin is never hidden behind an operator-scoped count.
export function DisableImpactChips({ pollingId, operatorId }: { pollingId: string; operatorId: string }) {
  const { data, isLoading } = usePollingConfigs(pollingId);
  const { mine, others } = partitionConfigsByOperator(data?.configs || [], operatorId);
  const mineSorted = [...mine].sort(byDestination);
  const othersSorted = [...others].sort(byDestination);
  const peerOperators = new Set(othersSorted.map((config) => config.techlogOperatorId));

  if (isLoading) {
    return <p>Checking impacted destinations…</p>;
  }

  const distinctDestinations = new Set(mineSorted.map((c) => c.destination))
    .size;

  return (
    <>
      {mineSorted.length === 0 && othersSorted.length === 0 && (
        <p>This stops polling for this source.</p>
      )}
      {mineSorted.length > 0 && (
        <>
          <p>
            {mineSorted.length === 1
              ? 'This stops the flow of data to this destination:'
              : distinctDestinations === mineSorted.length
                ? `This stops the flow of data to all ${mineSorted.length} destinations — not just one:`
                : `This stops all ${mineSorted.length} flows across ${distinctDestinations} destinations — not just one:`}
          </p>
          <span className="warning-chips">
            {mineSorted.map((config) => (
              <DestChip
                key={config.id}
                destination={config.destination}
                operatorLabel={config.destinationOperatorId}
              />
            ))}
          </span>
        </>
      )}
      {othersSorted.length > 0 && (
        <div className="shared-source-warning" data-testid="shared-source-warning">
          <p>
            This source is shared. Disabling it also stops the flow of data for{' '}
            {peerOperators.size === 1 ? 'another operator' : 'other operators'}:
          </p>
          <span className="warning-chips muted">
            {othersSorted.map((config) => (
              <DestChip
                key={config.id}
                destination={config.destination}
                operatorLabel={config.techlogOperatorId ?? undefined}
                muted
              />
            ))}
          </span>
        </div>
      )}
    </>
  );
}
